"use client"

import {
    Box,
    Heading,
    Text,
    HStack,
    Image,
    Badge,
    Grid,
    IconButton,
    Flex,
    useColorModeValue,
    Skeleton,
} from "@chakra-ui/react"
import { motion, AnimatePresence } from "framer-motion"
import { useRef, useState, useEffect } from "react"
import { StarIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { CodeBracketIcon, PaintBrushIcon, CpuChipIcon } from "@heroicons/react/24/outline"
import { fetchTestimonialsPublicAsync, selectTestimonials } from "../../../features/AdminPanel/Testimonials/TestimonialsSlice"
import { useDispatch, useSelector } from "react-redux"

const MotionBox = motion(Box)
const MotionFlex = motion(Flex)
const MotionHeading = motion(Heading)
const MotionText = motion(Text)
const MotionBadge = motion(Badge)
const MotionGrid = motion(Grid)

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
    },
}

const cardHover = {
    hover: {
        y: -5,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 10
        }
    }
}

export default function TestimonialsSection() {
    const dispatch = useDispatch()
    const { data: testimonialsData, loading, error } = useSelector((state) => state.testimonials)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isAutoPlaying, setIsAutoPlaying] = useState(true)
    const intervalRef = useRef(null)

    const bgSecondary = useColorModeValue("gray.50", "gray.800")
    const textPrimary = useColorModeValue("gray.800", "white")
    const textSecondary = useColorModeValue("gray.600", "gray.300")

    useEffect(() => {
        dispatch(fetchTestimonialsPublicAsync())
    }, [dispatch])

    const testimonials = testimonialsData?.map((testimonial, index) => {
        let category, categoryIcon
        switch (index % 3) {
            case 0:
                category = "DEV & IT"
                categoryIcon = CodeBracketIcon
                break
            case 1:
                category = "BUSINESS"
                categoryIcon = PaintBrushIcon
                break
            case 2:
                category = "HR & RECRUITMENT"
                categoryIcon = CpuChipIcon
                break
            default:
                category = "GENERAL"
                categoryIcon = CodeBracketIcon
        }

        return {
            id: testimonial.uuid,
            category,
            categoryIcon,
            testimonial: testimonial.review_description,
            rating: testimonial.review_rating,
            author: testimonial.name,
            role: `${testimonial.designation} at ${testimonial.company_name}`,
            date: new Date(testimonial.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }),
            avatar: testimonial.image || "/placeholder.svg?height=60&width=60",
            bgColor: ["green.50", "blue.50", "blue.50", "orange.50"][index % 4],
            accentColor: ["green.600", "blue.600", "blue.600", "orange.600"][index % 4],
        }
    }) || []

    const itemsPerPage = 2
    const totalPages = Math.ceil(testimonials.length / itemsPerPage)

    useEffect(() => {
        if (isAutoPlaying && testimonials.length > 0) {
            intervalRef.current = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % totalPages)
            }, 5000)
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
    }, [isAutoPlaying, totalPages, testimonials.length])

    const handlePrevious = () => {
        setIsAutoPlaying(false)
        setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages)
    }

    const handleNext = () => {
        setIsAutoPlaying(false)
        setCurrentIndex((prev) => (prev + 1) % totalPages)
    }

    const getCurrentTestimonials = () => {
        if (!testimonials.length) return []
        const startIndex = currentIndex * itemsPerPage
        return testimonials.slice(startIndex, startIndex + itemsPerPage)
    }

    const renderStars = (rating) => {
        return Array.from({ length: 5 }, (_, i) => (
            <MotionBox
                key={i}
                color={i < rating ? "orange.400" : "gray.300"}
                whileHover={{ scale: 1.2 }}
            >
                <StarIcon style={{
                    width: "16px",
                    height: "16px",
                    fill: i < rating ? "currentColor" : "none"
                }} />
            </MotionBox>
        ))
    }

    const renderSkeletonCards = () => {
        return Array(itemsPerPage).fill(0).map((_, index) => (
            <MotionBox
                key={index}
                bg="white"
                borderRadius="xl"
                boxShadow="lg"
                overflow="hidden"
                position="relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <Box p={6}>
                    <Skeleton height="20px" width="120px" mb={4} />
                    <Skeleton height="80px" mb={6} />
                    <Skeleton height="16px" width="100px" mb={6} />
                    <Flex justify="space-between" align="center">
                        <Box flex="1">
                            <Skeleton height="20px" width="180px" mb={2} />
                            <Skeleton height="16px" width="220px" mb={2} />
                            <Skeleton height="14px" width="100px" />
                        </Box>
                        <Box ml={4}>
                            <Skeleton width="48px" height="48px" borderRadius="full" />
                        </Box>
                    </Flex>
                </Box>
            </MotionBox>
        ))
    }

    return (
        <MotionBox
            as="section"
            py={{ base: 16, md: 24 }}
            bg={bgSecondary}
            position="relative"
            overflow="hidden"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
        >
            {/* Background decorative elements */}
            <MotionBox
                position="absolute"
                top="10%"
                right="5%"
                width="300px"
                height="300px"
                borderRadius="full"
                bg="blue.500"
                opacity="0.03"
                zIndex="0"
                animate={{
                    y: [0, -20, 0],
                    rotate: [0, 5, 0]
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />
            <MotionBox
                position="absolute"
                bottom="10%"
                left="5%"
                width="250px"
                height="250px"
                borderRadius="full"
                bg="indigo.200"
                opacity="0.05"
                zIndex="0"
                animate={{
                    y: [0, 20, 0],
                    rotate: [0, -5, 0]
                }}
                transition={{
                    duration: 12,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                }}
            />

            <Box px={{ base: 6, md: 12, lg: 24 }} mx="auto" w="full" maxW="none" position="relative" zIndex="1">
                <MotionBox
                    textAlign="center"
                    mb={{ base: 10, md: 16 }}
                    variants={fadeIn}
                >
                    <MotionBadge
                        colorScheme="blue"
                        bg="blue.100"
                        color="blue.800"
                        fontSize="sm"
                        px={3}
                        py={1}
                        borderRadius="full"
                        mb={4}
                        whileHover={{ scale: 1.05 }}
                    >
                        CLIENT TESTIMONIALS
                    </MotionBadge>
                    <MotionHeading
                        as="h2"
                        fontSize={{ base: "3xl", md: "4xl" }}
                        fontWeight="bold"
                        color={textPrimary}
                        mb={4}
                    >
                        What Our Clients{" "}
                        <Text
                            as="span"
                            className="bg-gradient-to-r from-indigo-500 to-blue-500 text-transparent bg-clip-text inline"
                        >
                            Say About Us
                        </Text>
                    </MotionHeading>
                    <MotionText
                        fontSize={{ base: "lg", md: "xl" }}
                        color={textSecondary}
                        maxW="3xl"
                        mx="auto"
                    >
                        Hear from employers who have transformed their hiring process with our platform.
                    </MotionText>
                </MotionBox>

                <Box position="relative">
                    {loading ? (
                        <MotionGrid
                            templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
                            gap={8}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ staggerChildren: 0.2 }}
                        >
                            {renderSkeletonCards()}
                        </MotionGrid>
                    ) : error ? (
                        <MotionBox
                            textAlign="center"
                            py={10}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <Text color="red.500">Error loading testimonials: {error}</Text>
                        </MotionBox>
                    ) : testimonials.length > 0 ? (
                        <>
                            <AnimatePresence mode="wait">
                                <MotionBox
                                    key={currentIndex}
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <MotionGrid
                                        templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
                                        gap={8}
                                    // variants={staggerContainer}
                                    >
                                        {getCurrentTestimonials().map((testimonial) => {
                                            const IconComponent = testimonial.categoryIcon
                                            return (
                                                <MotionBox
                                                    key={testimonial.id}
                                                    bg="white"
                                                    borderRadius="xl"
                                                    boxShadow="lg"
                                                    overflow="hidden"
                                                    position="relative"
                                                    whileHover="hover"
                                                    variants={cardHover}
                                                >
                                                    {/* Decorative background shapes */}
                                                    <MotionBox
                                                        position="absolute"
                                                        top="0"
                                                        right="0"
                                                        width="128px"
                                                        height="128px"
                                                        bgGradient="linear(to-br, blue.400, blue.500)"
                                                        borderBottomLeftRadius="full"
                                                        opacity="0.1"
                                                        whileHover={{ scale: 1.1 }}
                                                    />
                                                    <MotionBox
                                                        position="absolute"
                                                        bottom="0"
                                                        right="0"
                                                        width="96px"
                                                        height="96px"
                                                        bgGradient="linear(to-tl, blue.300, blue.400)"
                                                        borderTopLeftRadius="full"
                                                        opacity="0.15"
                                                        whileHover={{ scale: 1.1 }}
                                                    />

                                                    <Box p={6} position="relative" zIndex="2">
                                                        {/* Category Badge */}
                                                        <HStack spacing={2} mb={4}>
                                                            <MotionBox
                                                                color={testimonial.accentColor}
                                                                whileHover={{ rotate: 15 }}
                                                            >
                                                                <IconComponent style={{ width: "16px", height: "16px" }} />
                                                            </MotionBox>
                                                            <MotionBadge
                                                                bg={testimonial.bgColor}
                                                                color={testimonial.accentColor}
                                                                fontWeight="medium"
                                                                px={2}
                                                                py={1}
                                                                borderRadius="md"
                                                                whileHover={{ scale: 1.05 }}
                                                            >
                                                                {testimonial.category}
                                                            </MotionBadge>
                                                        </HStack>

                                                        {/* Testimonial Text */}
                                                        <MotionText
                                                            color="gray.700"
                                                            mb={6}
                                                            lineHeight="relaxed"
                                                            whileHover={{ scale: 1.01 }}
                                                        >
                                                            "{testimonial.testimonial}"
                                                        </MotionText>

                                                        {/* Rating */}
                                                        <HStack spacing={1} mb={6}>
                                                            {renderStars(testimonial.rating)}
                                                        </HStack>

                                                        {/* Author Info */}
                                                        <MotionFlex
                                                            justify="space-between"
                                                            align="center"
                                                            whileHover={{ x: 5 }}
                                                        >
                                                            <Box flex="1">
                                                                <Text fontWeight="semibold" color="gray.900" mb={1}>
                                                                    Work done by {testimonial.author}
                                                                </Text>
                                                                <Text fontSize="sm" color="gray.600" mb={1}>
                                                                    {testimonial.role}
                                                                </Text>
                                                                <Text fontSize="sm" color="gray.500">
                                                                    {testimonial.date}
                                                                </Text>
                                                            </Box>
                                                            {/* <Box ml={4}>
                                                                <MotionBox
                                                                    as={Image}
                                                                    src={testimonial.avatar}
                                                                    alt={testimonial.author}
                                                                    width="48px"
                                                                    height="48px"
                                                                    borderRadius="full"
                                                                    objectFit="cover"
                                                                    border="2px solid"
                                                                    borderColor="gray.200"
                                                                    fallbackSrc="/placeholder.svg?height=60&width=60"
                                                                    whileHover={{ scale: 1.1 }}
                                                                />
                                                            </Box> */}
                                                        </MotionFlex>
                                                    </Box>
                                                </MotionBox>
                                            )
                                        })}
                                    </MotionGrid>
                                </MotionBox>
                            </AnimatePresence>

                            {/* Navigation Controls */}
                            {totalPages > 1 && (
                                <>
                                    <MotionFlex
                                        justify="center"
                                        align="center"
                                        gap={4}
                                        mt={8}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.3 }}
                                    >
                                        <MotionBox
                                            as={IconButton}
                                            aria-label="Previous testimonials"
                                            icon={<ChevronLeftIcon style={{ width: "16px", height: "16px" }} />}
                                            variant="outline"
                                            borderRadius="full"
                                            onClick={handlePrevious}
                                            whileHover={{ scale: 1.1, bg: "gray.100" }}
                                        />

                                        {/* Pagination Dots */}
                                        <HStack spacing={2}>
                                            {Array.from({ length: totalPages }, (_, i) => (
                                                <MotionBox
                                                    key={i}
                                                    as="button"
                                                    w={i === currentIndex ? "24px" : "8px"}
                                                    h="8px"
                                                    borderRadius="full"
                                                    bg={i === currentIndex ? "green.500" : "gray.300"}
                                                    _hover={{ bg: i === currentIndex ? "green.500" : "gray.400" }}
                                                    onClick={() => {
                                                        setIsAutoPlaying(false)
                                                        setCurrentIndex(i)
                                                    }}
                                                    whileHover={{ scale: 1.2 }}
                                                    transition={{ type: "spring", stiffness: 400 }}
                                                />
                                            ))}
                                        </HStack>

                                        <MotionBox
                                            as={IconButton}
                                            aria-label="Next testimonials"
                                            icon={<ChevronRightIcon style={{ width: "16px", height: "16px" }} />}
                                            variant="outline"
                                            borderRadius="full"
                                            onClick={handleNext}
                                            whileHover={{ scale: 1.1, bg: "gray.100" }}
                                        />
                                    </MotionFlex>

                                    {/* Auto-play indicator */}
                                    <MotionBox
                                        textAlign="center"
                                        mt={4}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.4 }}
                                    >
                                        <Text
                                            as="button"
                                            fontSize="sm"
                                            color="gray.500"
                                            _hover={{ color: "gray.700" }}
                                            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                                        >
                                            {isAutoPlaying ? "⏸️ Pause auto-play" : "▶️ Resume auto-play"}
                                        </Text>
                                    </MotionBox>
                                </>
                            )}
                        </>
                    ) : (
                        <MotionBox
                            textAlign="center"
                            py={10}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <Text color={textSecondary}>No testimonials available at the moment.</Text>
                        </MotionBox>
                    )}
                </Box>
            </Box>
        </MotionBox>
    )
}